import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { AuditReport } from "@/components/AuditReport";

const TYPE_KEY = { REGULAR: "regular", VIP: "vip", EMPLOYEE: "employee" };
const LABEL = { REGULAR: "Regular", VIP: "VIP", EMPLOYEE: "Employee" };

function allRows(d) {
  return [
    ["Total Tickets", d.total.count],
    ["Regular Tickets", d.regular.count],
    ["VIP Tickets", d.vip.count],
    ["Employee Tickets", d.employee?.count ?? 0],
    ["Total Drinks Allocated", d.total.allowed],
    ["Total Drinks Redeemed", d.total.redeemed],
    ["Total Drinks Remaining", d.total.remaining],
    ["Regular Drinks Redeemed", d.regular.redeemed],
    ["VIP Drinks Redeemed", d.vip.redeemed],
    ["Employee Drinks Redeemed", d.employee?.redeemed ?? 0],
    ["Beers Served", d.sales?.redeemed_by_drink?.BEER ?? 0],
    ["Cocktails Served", d.sales?.redeemed_by_drink?.COCKTAIL ?? 0],
    ["Beers Sold", d.sales?.purchases_by_drink?.BEER ?? 0],
    ["Cocktails Sold", d.sales?.purchases_by_drink?.COCKTAIL ?? 0],
    ["Cash Transactions", d.sales?.purchases_by_payment?.CASH ?? 0],
    ["Card (Stripe) Transactions", d.sales?.purchases_by_payment?.STRIPE ?? 0],
    ["Cash Revenue", `$${(d.sales?.revenue_by_payment?.CASH ?? 0).toFixed(2)}`],
    ["Card (Stripe) Revenue", `$${(d.sales?.revenue_by_payment?.STRIPE ?? 0).toFixed(2)}`],
    ["Guest Self-Service / Stripe Transactions (included in Card)", d.sales?.self_service?.count ?? 0],
    ["Guest Self-Service / Stripe Revenue (included in Card)", `$${(d.sales?.self_service?.revenue ?? 0).toFixed(2)}`],
    ["Complimentary Drinks Redeemed", d.sales?.credits?.complimentary_redemptions ?? 0],
    ["Drinks Paid with Credit", d.sales?.credits?.credit_redemptions ?? 0],
    ["Earlier Purchased Drinks Redeemed", d.sales?.credits?.legacy_drink_redemptions ?? 0],
    ["Drink Credit Purchased", `$${(d.sales?.credits?.purchased ?? 0).toFixed(2)}`],
    ["Drink Credit Redeemed", `$${(d.sales?.credits?.redeemed ?? 0).toFixed(2)}`],
    ["Drink Credit Remaining", `$${(d.sales?.credits?.outstanding ?? 0).toFixed(2)}`],
    ["Total Revenue", `$${(d.sales?.revenue_total ?? 0).toFixed(2)}`],
    ["Tickets Never Scanned", d.counts.never_scanned],
    ["Tickets Partially Used", d.counts.partial],
    ["Tickets Fully Used", d.counts.fully_used],
  ];
}

function typeRows(b, label) {
  return [
    [`${label} Tickets`, b.count],
    [`${label} Base Drinks`, b.base],
    [`${label} Additional Purchased Drinks`, b.additional],
    [`${label} Total Drinks Allowed`, b.allowed],
    [`${label} Drinks Redeemed`, b.redeemed],
    [`${label} Drinks Remaining`, b.remaining],
    [`${label} Beers Redeemed`, b.redeemed_by_drink?.BEER ?? 0],
    [`${label} Cocktails Redeemed`, b.redeemed_by_drink?.COCKTAIL ?? 0],
  ];
}

export default function Reports() {
  const [d, setD] = useState(null);
  const [type, setType] = useState("ALL");
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-400">Loading…</div>;

  const bucket = d[TYPE_KEY[type]];
  const rows = type === "ALL" ? allRows(d) : bucket ? typeRows(bucket, LABEL[type]) : [];

  const download = async () => {
    const token = localStorage.getItem("fb_token");
    const qs = type === "ALL" ? "" : `?ticket_type=${type}`;
    const res = await fetch(`${API}/reports/export${qs}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `food_bazaar_tickets${type === "ALL" ? "" : `_${type.toLowerCase()}`}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Analytics</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">REPORTS</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44 bg-[#0B1120] border-slate-700 h-10 text-white" data-testid="reports-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Tickets</SelectItem>
              <SelectItem value="REGULAR">Regular</SelectItem>
              <SelectItem value="VIP">VIP</SelectItem>
              <SelectItem value="EMPLOYEE">Employee</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={download} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="reports-export-btn">
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl overflow-hidden">
        <div className="divide-y divide-slate-800" data-testid="reports-rows">
          {rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-2 px-6 py-4">
              <div className="text-slate-300">{k}</div>
              <div className="text-right font-display text-2xl font-black text-white">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <AuditReport />
    </div>
  );
}
